import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, AbstractControl } from '@angular/forms';
import { FormErrorComponent } from '../form-error/form-error.component';

export interface AutocompleteOption {
  value: any;
  label: string;
  icon?: string;
  description?: string;
}

/**
 * IsAutocompleteFieldComponent
 *
 * Campo de autocomplete com busca, dropdown de sugestões e navegação por teclado.
 *
 * @example
 * <is-autocomplete-field
 *   label="Buscar página"
 *   [options]="pages"
 *   placeholder="Digite para buscar..."
 *   (optionSelected)="onSelect($event)"
 * ></is-autocomplete-field>
 */
@Component({
  selector: 'is-autocomplete-field',
  standalone: true,
  imports: [CommonModule, FormsModule, FormErrorComponent],
  templateUrl: './autocomplete-field.component.html',
  styleUrls: ['./autocomplete-field.component.scss'],
  host: { '(document:mousedown)': 'onDocumentClick($event)' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteFieldComponent),
      multi: true,
    },
  ],
})
export class AutocompleteFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() options: AutocompleteOption[] = [];
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;
  @Input() prefixIcon = '';
  @Input() minChars = 1;
  @Input() maxResults = 8;

  @Output() optionSelected = new EventEmitter<AutocompleteOption>();

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('wrapperEl') wrapperEl?: ElementRef<HTMLDivElement>;

  searchTerm = '';
  filteredOptions: AutocompleteOption[] = [];
  isOpen = false;
  highlightedIndex = -1;
  isDisabled = false;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  // ─── ControlValueAccessor ───────────────────────────────────────────────────

  writeValue(val: any): void {
    this.searchTerm = val || '';
  }

  registerOnChange(fn: (val: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  // ─── Input Handling ─────────────────────────────────────────────────────────

  onInput(): void {
    this.onChange(this.searchTerm);

    if (this.searchTerm.length >= this.minChars) {
      this.filterOptions();
      this.isOpen = this.filteredOptions.length > 0;
      this.highlightedIndex = 0;
    } else {
      this.isOpen = false;
      this.filteredOptions = [];
    }
  }

  selectOption(option: AutocompleteOption): void {
    this.searchTerm = option.label;
    this.onChange(option.value);
    this.optionSelected.emit(option);
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  clear(): void {
    this.searchTerm = '';
    this.onChange('');
    this.isOpen = false;
    this.inputEl?.nativeElement?.focus();
  }

  // ─── Keyboard ───────────────────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen && this.searchTerm.length >= this.minChars) {
          this.filterOptions();
          this.isOpen = true;
        }
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.isOpen && this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        break;
    }
  }

  // ─── Click Outside ──────────────────────────────────────────────────────────

  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.wrapperEl?.nativeElement?.contains(event.target as HTMLElement)) {
      this.isOpen = false;
      this.onTouched();
    }
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private filterOptions(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredOptions = this.options
      .filter((o) => o.label.toLowerCase().includes(term) || (o.description?.toLowerCase().includes(term)))
      .slice(0, this.maxResults);
  }
}
